/**
 * Read every page of a paged Amplify query.
 *
 * For the surfaces that genuinely need the whole set — the facility picker and the user-form
 * dropdowns, which have to render every option — paging buys nothing, but the rows still arrive one
 * DynamoDB page at a time. Callers pass the name-ordered index query, so the result comes back
 * already sorted by the server and nothing needs reordering afterwards.
 */

// Rows per round trip. Well above the row count of any table this is used on, so the loop almost
// always exits after one call.
const BATCH = 500

// Runaway guard: a malformed or cyclic nextToken would otherwise loop forever.
const MAX_BATCHES = 20

export interface ListPage<T> {
	data?: T[] | null
	nextToken?: string | null
	errors?: { message: string }[] | null
}

export type ListFn<T> = (params: { limit: number; nextToken: string | null }) => Promise<ListPage<T>>

/**
 * Follow `nextToken` until the query is exhausted, preserving the order rows arrive in.
 *
 * Amplify returns ONE DynamoDB page per call — 100 rows by default — and reports a token for the
 * rest. Ignoring that token truncates silently, which is worse than an error: a clipped dropdown is
 * indistinguishable from the missing option not existing. Throws on the first GraphQL error for the
 * same reason.
 */
export async function listAll<T>(list: ListFn<T>): Promise<T[]> {
	const collected: T[] = []
	let nextToken: string | null = null

	for (let batch = 0; batch < MAX_BATCHES; batch++) {
		const response = await list({ limit: BATCH, nextToken })
		if (response.errors?.length) throw new Error(response.errors[0].message)
		collected.push(...(response.data ?? []))
		nextToken = response.nextToken ?? null
		if (!nextToken) break
	}

	return collected
}
